import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, Pill } from "../../components/domain/Bits";
import { InteractionForm } from "../../components/domain/InteractionForm";
import { InteractionTimeline, type MergedEvent } from "../../components/domain/InteractionTimeline";
import { TaskPanels } from "../../components/domain/TaskPanels";
import { EventPanels } from "../../components/domain/EventPanels";
import { OpportunityActivityLinker } from "../../components/domain/OpportunityActivityLinker";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  Building,
  CalendarDays,
  Contact2,
  DollarSign,
  ExternalLink,
  KanbanSquare,
  Layers,
  Plus,
  ShieldCheck,
  Target,
  Trophy,
} from "lucide-react";

const CANONICAL_ORDER = [
  "prospect_identified",
  "initial_contact",
  "qualified_opportunity",
  "discovery_complete",
  "solution_presented",
  "decision_pending",
  "closed_won",
  "closed_lost",
];

type OppTransition = {
  id: string;
  from_stage_id: string | null;
  to_stage_id: string;
  from_canonical: string | null;
  to_canonical: string;
  actor_user_id: string;
  occurred_at: string;
  reason: string | null;
};

type MilestoneSummary = {
  canonical_stage: string;
  first_reached_at: string | null;
  latest_reached_at: string | null;
  reached: boolean;
};

type OpportunityDetailResponse = {
  id: string;
  name: string;
  status: "open" | "won" | "lost" | "abandoned";
  archived: boolean;
  value: number;
  currency: string;
  canonical_stage: string;
  current_stage_id: string;
  current_stage_entered_at: string;
  expected_close_date: string | null;
  won_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  version: number;
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    lifecycle_status: string;
    archived: boolean;
  } | null;
  organization: { id: string; name: string; website: string | null; archived: boolean } | null;
  owner: { id: string; name: string; email: string; active: boolean } | null;
  pipeline: { id: string; code: string; label: string } | null;
  current_stage: {
    id: string;
    code: string;
    label: string;
    canonical_stage: string;
    order: number;
    probability: number;
  } | null;
  forecast: { value: number; probability: number; forecast_value: number };
  milestone_summary: MilestoneSummary[];
  transitions: OppTransition[];
  safe_provenance: {
    origin?: string;
    source_external_id?: string;
    source_created_at?: string;
    imported_at?: string;
    historical_attribution?: boolean;
  } | null;
};

function formatCurrency(n: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function OpportunityDetail() {
  const { opportunityId = "" } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<OpportunityDetailResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);
  const role = useEffectiveRole();
  const canLog = canAtLeast(role, "representative");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get<OpportunityDetailResponse>(`/crm/opportunities/${opportunityId}`);
        if (alive) setData(data);
      } catch (e: any) {
        if (e?.response?.status === 404) {
          if (alive) setNotFound(true);
        } else {
          toastError(e);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [opportunityId]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="opportunity-detail-loading">
        Loading opportunity…
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <>
        <PageHeader
          title="Opportunity not found"
          subtitle="This record may have been archived or belongs to another tenant."
          actions={
            <Button variant="outline" onClick={() => nav("/opportunities")} data-testid="opportunity-detail-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to opportunities
            </Button>
          }
        />
        <div className="max-w-4xl mx-auto p-6 md:p-8 text-sm text-slate-500" data-testid="opportunity-detail-not-found">
          We couldn’t find opportunity <span className="font-mono">{opportunityId}</span>.
        </div>
      </>
    );
  }

  const probPct = Math.round(data.forecast.probability * 100);

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <KanbanSquare className="w-6 h-6 text-slate-500" />
            {data.name}
            {data.archived && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-widest" data-testid="opportunity-detail-archived-badge">
                Archived
              </span>
            )}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {data.canonical_stage && (
              <Pill value={data.canonical_stage} type="stage" testId="opportunity-detail-stage" />
            )}
            <span className="text-xs text-slate-500">v{data.version}</span>
            <Pill value={data.status} type="status" testId="opportunity-detail-status" />
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canLog && (
              <Button
                onClick={() => setLogOpen(true)}
                data-testid="opportunity-detail-log-interaction"
                aria-label="Log an Interaction against this Opportunity"
              >
                <Plus className="w-4 h-4 mr-1" /> Log Interaction
              </Button>
            )}
            <Button variant="outline" onClick={() => nav("/opportunities")} data-testid="opportunity-detail-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        }
      />
      <div className="max-w-6xl mx-auto p-6 md:p-8 grid gap-6 md:grid-cols-3" data-testid="opportunity-detail">
        {/* Sidebar */}
        <aside className="md:col-span-1 space-y-4">
          <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="opportunity-detail-summary">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Deal summary
            </h2>
            <dl className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Value</dt>
                  <dd className="font-medium" data-testid="opportunity-detail-value">
                    {formatCurrency(data.value, data.currency)}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Target className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Forecast</dt>
                  <dd className="font-medium" data-testid="opportunity-detail-forecast">
                    {formatCurrency(data.forecast.forecast_value, data.currency)}
                    <span className="text-xs text-slate-500 ml-1">({probPct}% probability)</span>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Layers className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Pipeline / Stage</dt>
                  <dd className="font-medium" data-testid="opportunity-detail-pipeline">
                    {data.pipeline?.label || "—"}
                    {data.current_stage?.label && (
                      <span className="block text-xs text-slate-500">
                        Stage: {data.current_stage.label}
                      </span>
                    )}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarDays className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <dt className="text-xs text-slate-500">Dates</dt>
                  <dd className="font-medium space-y-0.5" data-testid="opportunity-detail-dates">
                    <div className="text-xs">
                      <span className="text-slate-500">Stage entered: </span>
                      {formatDateTime(data.current_stage_entered_at)}
                    </div>
                    <div className="text-xs">
                      <span className="text-slate-500">Expected close: </span>
                      {formatDate(data.expected_close_date)}
                    </div>
                    {data.won_at && (
                      <div className="text-xs text-emerald-700" data-testid="opportunity-detail-won-at">
                        <Trophy className="w-3 h-3 inline mr-1" />
                        Won: {formatDate(data.won_at)}
                      </div>
                    )}
                    {data.lost_at && (
                      <div className="text-xs text-rose-700" data-testid="opportunity-detail-lost-at">
                        Lost: {formatDate(data.lost_at)}
                        {data.lost_reason && ` (${data.lost_reason})`}
                      </div>
                    )}
                  </dd>
                </div>
              </div>
              {data.owner && (
                <div className="flex items-start gap-2" data-testid="opportunity-detail-owner">
                  <ShieldCheck className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <dt className="text-xs text-slate-500">Owner</dt>
                    <dd className="font-medium">
                      {data.owner.name}
                      <span className="block text-xs text-slate-500 break-all">{data.owner.email}</span>
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          </section>

          {/* Linked contact */}
          {data.contact && (
            <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="opportunity-detail-contact">
              <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                <Contact2 className="w-3.5 h-3.5" /> Contact
              </h2>
              <div className="text-sm font-medium">
                {`${data.contact.first_name} ${data.contact.last_name}`.trim()}
              </div>
              <div className="text-xs text-slate-500 break-all">
                {data.contact.email || data.contact.phone || "—"}
              </div>
              <Link
                to={`/contacts/${data.contact.id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
                data-testid="opportunity-detail-contact-open"
                aria-label={`Open contact ${data.contact.first_name} ${data.contact.last_name}`}
              >
                Open contact <ExternalLink className="w-3 h-3" />
              </Link>
            </section>
          )}
          {/* Linked organization */}
          {data.organization && (
            <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="opportunity-detail-org">
              <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                <Building className="w-3.5 h-3.5" /> Organization
              </h2>
              <div className="text-sm font-medium">{data.organization.name}</div>
              {data.organization.website && (
                <a
                  href={data.organization.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-600 hover:text-slate-900 break-all"
                >
                  {data.organization.website}
                </a>
              )}
            </section>
          )}

          {data.safe_provenance && (
            <section
              className="border border-slate-200 rounded-lg bg-slate-50/60 p-4 text-xs text-slate-600"
              data-testid="opportunity-detail-provenance"
            >
              <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Import provenance
              </h2>
              <div className="space-y-1">
                <div><span className="text-slate-500">Origin: </span>{data.safe_provenance.origin || "—"}</div>
                <div><span className="text-slate-500">Source ID: </span>{data.safe_provenance.source_external_id || "—"}</div>
                <div><span className="text-slate-500">Source created: </span>{formatDate(data.safe_provenance.source_created_at)}</div>
                <div><span className="text-slate-500">Imported: </span>{formatDate(data.safe_provenance.imported_at)}</div>
                {data.safe_provenance.historical_attribution && (
                  <div className="italic text-slate-500">Historical attribution — not a staff-generated transition.</div>
                )}
              </div>
            </section>
          )}
        </aside>

        {/* Main column */}
        <div className="md:col-span-2 space-y-6">
          {/* Merged Activity Timeline — Slice 1B. Interactions + Stage
              Transitions merged chronologically; milestones remain in
              their own separate panel below. */}
          <ActivityTimeline
            opportunityId={data.id}
            transitions={data.transitions}
            reloadKey={timelineKey}
            onChanged={() => setTimelineKey((k) => k + 1)}
          />

          {/* Slice 2A.1 — Tasks & Follow-Ups for this Opportunity. */}
          <TaskPanels scope={{ opportunity_id: data.id }} />
          <EventPanels scope={{ opportunity_id: data.id }} />
          <OpportunityActivityLinker
            opportunityId={data.id}
            contactId={data.contact?.id}
            organizationId={data.organization?.id}
            readOnly={!canLog}
          />

          {/* Stage-milestone summary */}
          <section className="border border-slate-200 rounded-lg bg-white" data-testid="opportunity-detail-milestones">
            <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Stage milestones</h2>
              <span className="text-xs text-slate-400">
                {data.milestone_summary.filter((m) => m.reached).length} of {CANONICAL_ORDER.length}
              </span>
            </header>
            <ul className="divide-y divide-slate-100">
              {data.milestone_summary.map((m) => (
                <li
                  key={m.canonical_stage}
                  className={`px-4 py-2.5 flex items-center gap-3 ${m.reached ? "" : "opacity-60"}`}
                  data-testid={`opportunity-detail-milestone-${m.canonical_stage}`}
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${m.reached ? "bg-emerald-500" : "bg-slate-300"}`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 capitalize">
                      {m.canonical_stage.replace(/_/g, " ")}
                    </div>
                    <div className="text-xs text-slate-500">
                      {m.reached ? (
                        <>
                          First: {formatDateTime(m.first_reached_at)}
                          {m.latest_reached_at && m.latest_reached_at !== m.first_reached_at && (
                            <span> · Latest: {formatDateTime(m.latest_reached_at)}</span>
                          )}
                        </>
                      ) : (
                        <span className="italic">Not reached</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
      <InteractionForm
        open={logOpen}
        onOpenChange={setLogOpen}
        onSuccess={() => setTimelineKey((k) => k + 1)}
        scope={{ opportunity_id: data.id }}
      />
    </>
  );
}

function ActivityTimeline({
  opportunityId, transitions, reloadKey, onChanged,
}: {
  opportunityId: string;
  transitions: OppTransition[];
  reloadKey: number;
  onChanged: () => void;
}) {
  const extra: MergedEvent[] = useMemo(
    () => transitions.map((t) => ({
      kind: "stage_transition" as const,
      occurred_at: t.occurred_at,
      data: {
        id: t.id, from_canonical: t.from_canonical, to_canonical: t.to_canonical,
        reason: t.reason, actor_user_id: t.actor_user_id,
      },
    })),
    [transitions],
  );
  return (
    <section className="border border-slate-200 rounded-lg bg-white" data-testid="opportunity-detail-activity">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Activity Timeline</h2>
        <span className="text-xs text-slate-400">
          Interactions + stage transitions
        </span>
      </header>
      <InteractionTimeline
        scope={{ opportunity_id: opportunityId }}
        reloadKey={reloadKey}
        onChanged={onChanged}
        extra={extra}
        emptyLabel="No activity yet."
      />
    </section>
  );
}
